<template>
  <div
    v-if="isToggle"
    class="chip chip-toggle"
    :class="{
      'chip-colored': colored,
      'chip-delete': canDelete,
    }"
    @click="clickChip"
  >
    <slot />
    <Button v-if="canDelete" severity="tertiary">
      <template #iconLeft><IconRemove /></template>
    </Button>
  </div>
  <div
    v-else
    class="chip"
    :class="{
      'chip-colored': colored,
      'chip-delete': canDelete,
      'chip-static': isStatic,
    }"
  >
    <slot />
    <Button v-if="canDelete" @click="clickChip" severity="tertiary">
      <template #iconLeft><IconRemove /></template>
    </Button>
    <IconCheckSmall v-if="isStatic" class="icon-check" />
  </div>
</template>

<script lang="ts" setup>
import Button from './Button.vue';

import IconRemove from '@/icons/IconChipRemove.svg?component';
import IconCheckSmall from '@/icons/IconCheckSmall.svg?component';

const props = withDefaults(
  defineProps<{
    id?: number | null;
    /**
     * If the chip can be removed (eg: It's a chosen option from a Multiselect)
     */
    canDelete?: boolean;
    /**
     * Colored chips have a background & prominent border
     */
    colored?: boolean;
    /**
     * Read only. Eg: Event Review
     */
    isStatic?: boolean;
    /**
     * Whether it is itself a checkbox. Eg: Zone Categories
     */
    isToggle?: boolean;
  }>(),
  {
    canDelete: false,
    colored: false,
    isStatic: false,
    isToggle: false,
  },
);

const emit = defineEmits(['chipClicked']);

const clickChip = () => {
  emit('chipClicked', props.id);
};
</script>

<style lang="scss">
.chip {
  @include mixins.rounded-border($size: variables.$sizing-xxxs);
  @include sizing.padding('xxxs');
  @include typography.font-body($weight: normal, $size: sm);
  align-items: center;
  background: variables.$primitives-color-border-100;
  display: inline-flex;
  gap: variables.$sizing-xxxs;
  margin: 0 auto;
  text-align: center;

  .button--tertiary {
    min-height: 20px;
    min-width: 20px;
    padding: 0;
  }
}

.chip-toggle {
  @include mixins.rounded-border();
  cursor: pointer;
  margin: 0;
}

.chip-colored {
  @include mixins.rounded-border($color: variables.$primitives-color-primary-200);
  background: variables.$primitives-color-background-300;
  color: variables.$primitives-color-primary-500;
  height: 32px;
}

.chip-static {
  @include mixins.rounded-border(
    $color: variables.$primitives-color-border-100,
    $size: variables.$sizing-sm
  );
  @include sizing.padding('xxxs', 'xs');
  background: variables.$primitives-color-background-100;

  .icon-check {
    color: variables.$primitives-color-primary-500;
    flex-shrink: 0;
  }
}

.chips-container {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxs, $wrap: true);

  > .chip {
    margin: 0;
  }
}
</style>
